export function calcHourlyBookingDetails(params) {
    var _a;
    const { duration, startDate, currentSeason, dayOfWeek, discountType, giftCardDiscount, discount, selectedGlobalAddons, selectedBoatAddons, boatRefresh, TAX_AND_FEE_RATE, taxes, depositPercentage, } = params;
    if (!startDate || !currentSeason) {
        return {
            bookingDetails: getEmptyBookingDetails(taxes, depositPercentage),
            error: 'Missing season or start date.',
        };
    }
    // Hourly data for the given day
    const hourData = (_a = currentSeason === null || currentSeason === void 0 ? void 0 : currentSeason.hourlyPrices) === null || _a === void 0 ? void 0 : _a[dayOfWeek];
    if (!hourData) {
        return {
            bookingDetails: getEmptyBookingDetails(taxes, depositPercentage),
            error: `No hourly pricing found for ${dayOfWeek}.`,
        };
    }
    // Validate min/max hours
    if (duration < hourData.minimumHours) {
        return {
            bookingDetails: getEmptyBookingDetails(taxes, depositPercentage),
            error: `Minimum ${hourData.minimumHours} hour(s) on ${dayOfWeek}.`,
        };
    }
    if (duration > hourData.maximumHours) {
        return {
            bookingDetails: getEmptyBookingDetails(taxes, depositPercentage),
            error: `Max ${hourData.maximumHours} hour(s) on ${dayOfWeek}.`,
        };
    }
    // Determine finalRate from multi-hour discount
    let finalRate = hourData.basePrice;
    const hourDiscounts = hourData.multiHourDiscounts || [];
    for (const item of hourDiscounts) {
        if (duration >= item.hours) {
            finalRate = item.pricePerHour;
        }
    }
    const baseSubtotal = finalRate * duration;
    // Calculate boatAddons
    let boatAddonTotal = 0;
    selectedBoatAddons.forEach((addon) => {
        var _a;
        const found = (_a = boatRefresh.Addons) === null || _a === void 0 ? void 0 : _a.find((b) => b.name === addon.name);
        if (found) {
            boatAddonTotal += found.price * addon.quantity;
        }
    });
    // Calculate globalAddons
    let globalAddonTotal = 0;
    selectedGlobalAddons.forEach((addon) => {
        const price = Math.max(baseSubtotal * addon.percentage, addon.minimum);
        globalAddonTotal += price;
    });
    // For consistency, if you also want to apply an extra "discount" prop to hourly:
    const discountValue = discountType === '%' ? (baseSubtotal * discount) / 100 : discount;
    // Gift card logic
    const giftCardValue = discountType === '%'
        ? baseSubtotal * (giftCardDiscount / 100)
        : giftCardDiscount;
    // Platform + taxes
    const feesCalc = (baseSubtotal + boatAddonTotal - giftCardValue - discountValue) * TAX_AND_FEE_RATE;
    const totalPriceCalc = baseSubtotal +
        boatAddonTotal +
        feesCalc +
        globalAddonTotal -
        discountValue -
        giftCardValue;
    // Deposit logic
    const depositDueNow = totalPriceCalc * (depositPercentage / 100);
    const remainingBalance = totalPriceCalc - depositDueNow;
    const bookingDetails = {
        subtotal: baseSubtotal,
        daysBooked: 0,
        hoursBooked: duration,
        averagePrice: finalRate,
        fees: feesCalc + globalAddonTotal,
        discount: 0, // or discount if you treat it as multi-day discount
        discountValue,
        giftCardAmount: giftCardValue,
        totalPrice: totalPriceCalc,
        addonsTotal: boatAddonTotal,
        tax: taxes,
        depositPercentage,
        depositDueNow,
        remainingBalance,
    };
    return { bookingDetails };
}
export function calcDailyBookingDetails(params) {
    var _a;
    const { startDate, endDate, currentSeason, minDays, maxDays, discountType, giftCardDiscount, discount, selectedGlobalAddons, selectedBoatAddons, boatRefresh, totalTaxRate, fee, depositPercentage, } = params;
    if (!startDate || !endDate || !currentSeason) {
        return {
            bookingDetails: getEmptyBookingDetails([], depositPercentage),
            error: 'Missing season or dates.',
        };
    }
    const dayDiff = endDate.diff(startDate, 'day') + 1;
    if (dayDiff < minDays) {
        return {
            bookingDetails: getEmptyBookingDetails([], depositPercentage),
            error: `You must book at least ${minDays} day(s).`,
        };
    }
    if (dayDiff > maxDays) {
        return {
            bookingDetails: getEmptyBookingDetails([], depositPercentage),
            error: `You cannot book more than ${maxDays} day(s).`,
        };
    }
    // Calculate the base subtotal by summing daily prices day-by-day
    let baseSubtotal = 0;
    for (let i = 0; i < dayDiff; i++) {
        const thisDayOfWeek = startDate.add(i, 'day').format('dddd');
        const dailyPrice = ((_a = currentSeason === null || currentSeason === void 0 ? void 0 : currentSeason.dailyPrices) === null || _a === void 0 ? void 0 : _a[thisDayOfWeek]) || 0;
        baseSubtotal += dailyPrice;
    }
    // Multi-day discount from the season
    let multiDayDiscountPct = 0;
    const multiDayDiscounts = currentSeason.multiDayDiscounts || [];
    for (const dItem of multiDayDiscounts) {
        const threshold = parseInt(Object.keys(dItem)[0]);
        const pct = Object.values(dItem)[0];
        if (dayDiff >= threshold && pct > multiDayDiscountPct) {
            multiDayDiscountPct = pct;
        }
    }
    // Apply multi-day discount
    const discountedSubtotal = baseSubtotal * (1 - multiDayDiscountPct / 100);
    // Add boat addons
    let addonsTotalCalc = 0;
    selectedBoatAddons.forEach((addon) => {
        var _a;
        const found = (_a = boatRefresh.Addons) === null || _a === void 0 ? void 0 : _a.find((b) => b.name === addon.name);
        if (found) {
            addonsTotalCalc += found.price * addon.quantity;
        }
    });
    // Global add-ons
    let globalAddonsTotal = 0;
    selectedGlobalAddons.forEach((addon) => {
        const price = Math.max(discountedSubtotal * addon.percentage, addon.minimum);
        globalAddonsTotal += price;
    });
    // Additional discount (e.g., from a "season discount" or user-level discount)
    const discountValue = discountType === '%'
        ? (discountedSubtotal * discount) / 100
        : discount;
    // Gift card
    const giftCardValue = discountType === '%'
        ? discountedSubtotal * (giftCardDiscount / 100)
        : giftCardDiscount;
    const finalSubtotal = discountedSubtotal + addonsTotalCalc;
    const combinedTaxFee = fee + (totalTaxRate / 100);
    const taxedAndFeeBase = finalSubtotal - discountValue - giftCardValue;
    const toChargeFee = taxedAndFeeBase * combinedTaxFee + globalAddonsTotal;
    const total = finalSubtotal + toChargeFee - discountValue - giftCardValue;
    // Deposit logic
    const depositDueNow = total * (depositPercentage / 100);
    const remainingBalance = total - depositDueNow;
    const bookingDetails = {
        subtotal: discountedSubtotal,
        daysBooked: dayDiff,
        hoursBooked: 0,
        averagePrice: dayDiff > 0 ? discountedSubtotal / dayDiff : 0,
        fees: toChargeFee,
        discount: multiDayDiscountPct, // This is the multi-day discount
        discountValue, // Additional discount from props
        giftCardAmount: giftCardValue,
        totalPrice: total,
        addonsTotal: addonsTotalCalc,
        giftCardDiscount,
        discountType,
        tax: [], // If you need the raw tax breakdown, pass it in or store it
        depositPercentage,
        depositDueNow,
        remainingBalance,
    };
    return { bookingDetails };
}
/**
 * Helper to return an “empty” booking details object.
 */
function getEmptyBookingDetails(tax, depositPercentage) {
    return {
        subtotal: 0,
        daysBooked: 0,
        hoursBooked: 0,
        averagePrice: 0,
        fees: 0,
        discount: 0,
        discountValue: 0,
        giftCardAmount: 0,
        totalPrice: 0,
        addonsTotal: 0,
        tax,
        depositPercentage,
        depositDueNow: 0,
        remainingBalance: 0,
    };
}
